<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px;">
      <div class="card card-sm">
        <!-- ================= FILTROS ================= -->
        <div class="card-header" style="background-color: #DBECDB;">
          <h5 class="mb-2"><i class="fa fa-tasks me-1"></i> Estado actual de trámites</h5>

          <div class="row mb-2">
            <div class="col-md-4">
              <div class="input-group">
                <label>Unidad:</label>
                <select v-model="filters.id_unidad" class="form-select">
                  <option value="">Todas las unidades</option>
                  <option v-for="u in unidades" :key="u.id" :value="u.id">
                    {{ u.nombre_unidad }} ({{ u.codigo }})
                  </option>
                </select>
              </div>
            </div>

            <div class="col-md-4">
              <div class="input-group">
                <label>Sección:</label>
                <select v-model="filters.id_area" class="form-select" :disabled="!filters.id_unidad">
                  <option value="">Todas las secciones</option>
                  <option v-for="a in areas" :key="a.id" :value="a.id">
                    ({{ a.codigo }}) {{ a.nombre_area }}
                  </option>
                </select>
              </div>
            </div>

            <div class="col-md-4">
              <div class="input-group">
                <label>Trámite:</label>
                <select v-model="filters.tipotramite" class="form-select">
                  <option value="">Todos</option>
                  <option v-for="t in tipotramites" :key="t.id" :value="t.id">
                    {{ t.tipo }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col-md-2">
              <div class="input-group">
                <label>Gestión:</label>
                <select v-model="filters.gestion" class="form-select">
                  <option value="">Todas</option>
                  <option v-for="y in gestiones" :key="y" :value="y">{{ y }}</option>
                </select>
              </div>
            </div>

            <div class="col-md-2">
              <div class="input-group">
                <label>Etapa:</label>
                <select v-model="filters.etapa" class="form-select">
                  <option value="">Todas</option>
                  <option v-for="e in etapas" :key="e.descripcion" :value="e.descripcion">
                    {{ e.descripcion }}
                  </option>
                </select>
              </div>
            </div>

            <div class="col-md-2">
              <div class="input-group">
                <label>Estado:</label>
                <select v-model="filters.estado" class="form-select">
                  <option value="">Todos</option>
                  <option v-for="s in estados" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>
            </div>

            <div class="col-md-2">
              <div class="input-group">
                <label>Fecha de:</label>
                <select v-model="filters.tipo_fecha" class="form-select">
                  <option value="actual">Etapa actual</option>
                  <option value="registro">Registro</option>
                </select>
              </div>
            </div>

            <div class="col-md-2">
              <div class="input-group">
                <label>Desde:</label>
                <input type="date" v-model="filters.desde" class="form-control" />
              </div>
            </div>

            <div class="col-md-2">
              <div class="input-group">
                <label>Hasta:</label>
                <input type="date" v-model="filters.hasta" class="form-control" />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <BButton size="sm" variant="light" @click="limpiar">
              <i class="fa fa-eraser"></i> Limpiar
            </BButton>
            <BButton size="sm" variant="secondary" @click="buscar">
              <i class="fa fa-search"></i> Buscar
            </BButton>
            <BButton size="sm" variant="danger" @click="generarPDF" :disabled="pagination.total === 0">
              <i class="fa fa-file-pdf me-1"></i> PDF
            </BButton>
          </div>
        </div>

        <!-- ================= RESULTADOS ================= -->
        <div class="card-body pt-2">
          <!-- Resumen por etapa (clic para filtrar) -->
          <div class="d-flex flex-wrap gap-2 mb-2" v-if="!loading && resumen.length">
            <div
              v-for="r in resumen"
              :key="r.etapa"
              class="resumen-card"
              :class="{ activo: filters.etapa === r.etapa }"
              @click="filtrarEtapa(r.etapa)"
              title="Filtrar por esta etapa"
            >
              <div class="num">{{ r.cantidad }}</div>
              <div class="lbl">{{ r.etapa }}</div>
            </div>
            <div class="resumen-card total">
              <div class="num">{{ pagination.total }}</div>
              <div class="lbl">Total</div>
            </div>
          </div>

          <div v-if="loading" class="text-center py-3">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2">Cargando...</p>
          </div>

          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered" style="font-size: 0.8rem;">
                <thead>
                  <tr>
                    <th class="text-center">#</th>
                    <th>Código</th>
                    <th>Tipo Trámite</th>
                    <th>Solicitado por</th>
                    <th class="text-center">Etapa actual</th>
                    <th class="text-center">Estado actual</th>
                    <th class="text-center">Fecha actual</th>
                    <th>Responsable actual</th>
                    <th class="text-center" title="Días en la etapa actual">Días</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in items" :key="item.id">
                    <td class="text-center">{{ pagination.from + i }}</td>
                    <td class="text-nowrap">{{ item.codigo }}</td>
                    <td>{{ item.tipo_tramite }}</td>
                    <td>{{ item.solicitante || 'N/A' }}</td>
                    <td class="text-center">{{ item.etapa_actual }}</td>
                    <td class="text-center">
                      <span class="badge" :class="claseEstado(item.estado_actual)">
                        {{ item.estado_actual }}
                      </span>
                    </td>
                    <td class="text-center text-nowrap">{{ item.fecha_actual }}</td>
                    <td>
                      {{ item.responsable_actual }}
                      <div v-if="item.observacion" class="text-muted small">
                        <i class="fa fa-comment-dots"></i> {{ item.observacion }}
                      </div>
                    </td>
                    <td class="text-center">
                      <span :class="{ 'text-danger fw-bold': item.dias_en_etapa > 15 }">
                        {{ item.dias_en_etapa }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="items.length === 0">
                    <td colspan="9" class="text-center py-4">
                      No hay trámites con los filtros seleccionados
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="row mt-2" v-if="pagination.total > 0">
              <div class="col-md-6">
                <p class="text-muted mb-0">
                  Mostrando {{ pagination.from }} - {{ pagination.to }} de {{ pagination.total }} trámites
                </p>
              </div>
              <div class="col-md-6">
                <BPagination
                  v-model="pagination.current_page"
                  :total-rows="pagination.total"
                  :per-page="pagination.per_page"
                  @page-click="cambiarPagina"
                  align="right"
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BButton, BPagination } from 'bootstrap-vue-3';

export default {
  name: 'ReporteEstadoTramite',
  components: { BButton, BPagination },

  setup() {
    const year = new Date().getFullYear();
    const gestiones = [year - 3, year - 2, year - 1, year];

    // Fecha local en formato YYYY-MM-DD (evita el desfase UTC de toISOString)
    const fechaLocal = (d) => {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    };
    const inicioMes = () => {
      const hoy = new Date();
      return fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
    };
    const hoy = () => fechaLocal(new Date());

    const filtrosIniciales = () => ({
      id_unidad: '',
      id_area: '',
      tipotramite: '',
      gestion: '',
      etapa: '',
      estado: '',
      tipo_fecha: 'actual',
      desde: inicioMes(),   // por defecto: primer día del mes actual
      hasta: hoy()          // por defecto: fecha actual
    });

    const filters = reactive(filtrosIniciales());
    const items = ref([]);
    const resumen = ref([]);
    const loading = ref(false);

    const unidades = ref([]);
    const areas = ref([]);
    const tipotramites = ref([]);
    const etapas = ref([]);
    const estados = ref([]);

    const pagination = reactive({
      current_page: 1, per_page: 15, total: 0, from: 0, to: 0
    });

    // Solo envía los filtros con valor
    const construirParams = () => {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });
      return params;
    };

    const validarFechas = () => {
      if (filters.desde && filters.hasta && filters.desde > filters.hasta) {
        Swal.fire({ icon: 'warning', title: 'Fechas', text: 'La fecha "Desde" no puede ser mayor a "Hasta".' });
        return false;
      }
      return true;
    };

    // ============ DATOS ============
    const fetchData = async () => {
      if (!validarFechas()) return;
      loading.value = true;
      try {
        const { data } = await axios.get('/reportes/estadotramite', {
          params: {
            ...construirParams(),
            page: pagination.current_page,
            per_page: pagination.per_page
          }
        });

        if (data.success) {
          const d = data.datos;
          items.value = d.data || [];
          pagination.current_page = d.current_page;
          pagination.per_page = d.per_page;
          pagination.total = d.total;
          pagination.from = d.from || 0;
          pagination.to = d.to || 0;
          resumen.value = data.resumen || [];
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los datos' });
      } finally {
        loading.value = false;
      }
    };

    const generarPDF = async () => {
      if (!validarFechas()) return;
      try {
        Swal.fire({
          title: 'Generando PDF',
          text: 'Por favor espere...',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });

        const response = await axios.get('/reportes/estadotramitepdf', {
          params: construirParams(),
          responseType: 'blob',
          timeout: 300000
        });

        Swal.close();
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el PDF.' });
      }
    };

    // ============ CATÁLOGOS ============
    const fetchUnidades = async () => {
      const { data } = await axios.get('/unidad/select');
      unidades.value = data.unidades || [];
    };

    const fetchAreas = async () => {
      if (!filters.id_unidad) { areas.value = []; return; }
      const { data } = await axios.get('/area/select', { params: { id_unidad: filters.id_unidad } });
      areas.value = data.areas || [];
    };

    const fetchTipotramites = async () => {
      const params = {};
      if (filters.id_unidad) params.id_unidad = filters.id_unidad;
      if (filters.id_area) params.id_area = filters.id_area;
      const { data } = await axios.get('/tipotramite/selectreporte', { params });
      tipotramites.value = data.tipotramites || [];
    };

    const fetchEtapas = async () => {
      const { data } = await axios.get('/reportes/etapas');
      etapas.value = data.etapas || [];
    };

    const fetchEstados = async () => {
      const { data } = await axios.get('/reportes/estados');
      estados.value = data.estados || [];
    };

    // ============ HANDLERS ============
    const buscar = () => { pagination.current_page = 1; fetchData(); };

    const cambiarPagina = (event, page) => {
      event.preventDefault();
      pagination.current_page = page;
      fetchData();
    };

    const filtrarEtapa = (etapa) => {
      filters.etapa = filters.etapa === etapa ? '' : etapa;
      buscar();
    };

    const limpiar = () => {
      Object.assign(filters, filtrosIniciales());
      buscar();
    };

    const claseEstado = (estado) => {
      switch ((estado || '').toLowerCase()) {
        case 'recibido':
        case 'recepcionado': return 'bg-primary';
        case 'derivado':
        case 'enviado':      return 'bg-purple';
        case 'aprobado':     return 'bg-success';
        case 'observado':    return 'bg-warning text-dark';
        case 'rechazado':    return 'bg-danger';
        case 'entregado':    return 'bg-teal';
        default:             return 'bg-secondary';
      }
    };

    // Cascada unidad -> sección -> tipo de trámite
    watch(() => filters.id_unidad, () => {
      filters.id_area = '';
      filters.tipotramite = '';
      fetchAreas();
      fetchTipotramites();
    });

    watch(() => filters.id_area, () => {
      filters.tipotramite = '';
      fetchTipotramites();
    });

    onMounted(() => {
      Promise.allSettled([fetchUnidades(), fetchTipotramites(), fetchEtapas(), fetchEstados()]);
      fetchData();
    });

    return {
      filters, items, resumen, loading, pagination, gestiones,
      unidades, areas, tipotramites, etapas, estados,
      buscar, cambiarPagina, filtrarEtapa, limpiar, generarPDF, claseEstado
    };
  }
};
</script>

<style scoped>
thead th {
  background-color: #D84315;
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
  font-size: 0.75rem;
  white-space: nowrap;
}
tbody td { padding: 6px !important; vertical-align: middle; }

.table-responsive {
  max-height: 520px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.card-header .input-group { align-items: center; gap: 5px; flex-wrap: nowrap; }
.card-header label { min-width: 60px; margin-bottom: 0; font-weight: 500; font-size: 0.85rem; }
.card-header select, .card-header input { font-size: 0.85rem; }

.resumen-card {
  min-width: 95px;
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-left: 4px solid #D84315;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s;
}
.resumen-card:hover { background: #fff3ee; }
.resumen-card.activo { background: #D84315; color: #fff; }
.resumen-card.total { border-left-color: #4facfe; cursor: default; }
.resumen-card .num { font-size: 1.15rem; font-weight: bold; line-height: 1.1; }
.resumen-card .lbl { font-size: 0.75rem; }

.bg-purple { background-color: #6f42c1 !important; }
.bg-teal { background-color: #20c997 !important; }
</style>
